// framework/client/helpers/MessagingClient/MessageInput.js
import { ComponentBase } from "../../../core/ComponentBase.js";

export class MessageInput extends ComponentBase {
    static styles = `
        .message-input-wrapper {
            display: flex;
            align-items: flex-end; /* Align items to bottom for multi-line textarea */
            padding: 0.75rem 1rem; /* Increased padding for better spacing */
            border-top: 1px solid var(--bs-border-color);
            background-color: var(--bs-tertiary-bg); /* Subtle background for input area */
            flex-shrink: 0; /* Prevent shrinking */
            gap: 0.5rem; /* Gap between elements */
        }
        .message-input-wrapper .attach-btn, .message-input-wrapper .send-btn {
            flex-shrink: 0; /* Prevent buttons from shrinking */
            align-self: flex-end; /* Align buttons to the bottom of the flex container */
            margin-bottom: 0.375rem; /* Align with textarea padding */
        }
        .message-input-wrapper textarea.message-text-area { /* Target textarea specifically */
            flex-grow: 1;
            border-radius: 18px; /* Rounded textarea */
            padding: 0.6rem 1rem; /* Adjusted padding for better text alignment */
            resize: none; /* Disable manual resize */
            max-height: 120px; /* Limit max height */
            min-height: calc(1.5em + 1.2rem + 2px); /* Match Bootstrap's default single line input height */
            overflow-y: auto; /* Scroll for long messages */
            line-height: 1.5; /* Standard line height */
            border-color: var(--bs-border-color-translucent);
            background-color: var(--bs-body-bg); /* Match main chat area bg for consistency */
            color: var(--bs-body-color);
        }
         .message-input-wrapper textarea.message-text-area:focus {
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb, 0,123,255), 0.25); /* Ensure fallback for primary-rgb */
         }
        .input-actions button { /* This class seems unused based on HTML, styling attach/send directly */
            background: none;
            border: none;
            color: var(--bs-secondary-color);
            font-size: 1.5rem; /* Larger icons for actions */
            padding: 0.5rem;
            /* margin-left: 0.5rem; -- Handled by gap on wrapper */
            line-height: 1; /* Ensure icons align well */
        }
        .input-actions button:hover {
            color: var(--bs-primary);
        }

        /* Styling for specific buttons */
        .message-input-wrapper .attach-btn {
            color: var(--bs-secondary-color);
        }
        .message-input-wrapper .attach-btn:hover {
            color: var(--bs-primary);
        }
        .message-input-wrapper .send-btn {
             color: var(--bs-primary);
         }
          .message-input-wrapper .send-btn:hover {
             color: color-mix(in srgb, var(--bs-primary, #0d6efd), black 15%);
         }
    `;

    constructor(config) { // Expects { appClient, instanceId }
        super(config);
        // DOM Elements
        this.textarea = null;
        this.sendButton = null;
        this.attachButton = null;
        // this.emojiButton = null; // For future

        this.typingTimeout = null;
        this.isCurrentlyTyping = false;
        this.TYPING_DEBOUNCE_MS = 700; // User considered stopped typing after this delay
        this.TYPING_BROADCAST_INTERVAL_MS = 2500; // How often to send "still typing" if continuously typing
        this.lastTypingBroadcastTime = 0;


        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.container = document.createElement('div');
        this.container.className = 'message-input-wrapper';
        this.renderContent();
        this.attachEventListeners();
    }

    renderContent() {
        this.container.innerHTML = `
            <button class="btn btn-link attach-btn p-0" title="${this.t('Attach File')}" data-i18n-title="Attach File"><i class="bi bi-paperclip fs-4"></i></button>
            <textarea class="form-control message-text-area" placeholder="${this.t('Type a message...')}" data-i18n-placeholder="Type a message..." rows="1"></textarea>
            <button class="btn btn-link send-btn p-0" title="${this.t('Send')}" data-i18n-title="Send"><i class="bi bi-send-fill fs-4"></i></button>
        `;
        this.textarea = this.container.querySelector('.message-text-area');
        this.sendButton = this.container.querySelector('.send-btn');
        this.attachButton = this.container.querySelector('.attach-btn');
        this.appClient.tx.applyTranslations(this.container);
    }

    attachEventListeners() {
        this.sendButton.addEventListener('click', this.handleSendMessage.bind(this));
        this.textarea.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSendMessage();
            }
        });
        this.textarea.addEventListener('input', this.handleTextareaInput.bind(this)); // Changed listener
        this.attachButton.addEventListener('click', this.handleAttachFile.bind(this));
    }

    handleTextareaInput() {
        this.autoResizeTextarea(); // Keep auto-resize

        if (!this.isCurrentlyTyping) {
            this.isCurrentlyTyping = true;
            this.sendTypingStatus(true);
            this.lastTypingBroadcastTime = Date.now();
        } else {
            // If continuously typing, send "still typing" periodically
            if (Date.now() - this.lastTypingBroadcastTime > this.TYPING_BROADCAST_INTERVAL_MS) {
                this.sendTypingStatus(true); // Re-affirm typing status
                this.lastTypingBroadcastTime = Date.now();
            }
        }

        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.isCurrentlyTyping = false;
            this.sendTypingStatus(false);
        }, this.TYPING_DEBOUNCE_MS);
    }

    sendTypingStatus(isTyping) {
        // This method will be called by the parent (MessagingClient)
        // which then calls the RPC. MessageInput itself doesn't know the conversationId.
        this.emit('typingStatusChanged', { isTyping });
    }

    handleSendMessage() {
        const text = this.textarea.value.trim();
        if (text) {
            this.emit('messageSent', { text });
            this.textarea.value = '';
            this.autoResizeTextarea();

            // --- ADD: Stop typing indicator on send ---
            clearTimeout(this.typingTimeout);
            if (this.isCurrentlyTyping) {
                this.isCurrentlyTyping = false;
                this.sendTypingStatus(false);
            }
            // --- END ADD ---
        }
    }

    handleAttachFile() {
        // This would open a file picker. For P1, we can just log it.
        // Actual file upload would involve a FileUpload component or similar logic.
        this.log.info("Attach file clicked - (File Upload UI to be implemented)");
        this.appClient.notifier.info("File attachment UI is not yet implemented for Phase 1.");
        // Example of how you might integrate FileUpload later:
        // const fileUpload = new FileUpload({ appClient: this.appClient, getUploadKey: async () => { ... } });
        // fileUpload.openFile();
        // fileUpload.on('uploadCompleted', (fileInfo) => this.emit('messageSent', { attachments: [fileInfo] }));
    }

    autoResizeTextarea() {
        this.textarea.style.height = 'auto'; // Reset height to shrink if text is deleted
        // Set a slight delay to ensure scrollHeight is calculated after value update
        requestAnimationFrame(() => {
            this.textarea.style.height = `${this.textarea.scrollHeight}px`;
        });
    }

    render() {
        return this.container;
    }

    destroy() {
        clearTimeout(this.typingTimeout); // Clear timeout on destroy
        this.textarea?.removeEventListener('input', this.handleTextareaInput);
        this.sendButton?.removeEventListener('click', this.handleSendMessage);
        this.textarea?.removeEventListener('keypress', () => { });
        this.textarea?.removeEventListener('input', this.autoResizeTextarea);
        this.attachButton?.removeEventListener('click', this.handleAttachFile);
        super.destroy();
    }
}